// src/components/TechnicianTabsWrapper.js
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tab, Box, Paper, Button, Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

import TabCalderas from './technician-tabs/TabCalderas';
import TabIntroductor from './technician-tabs/TabIntroductor';
import TabHibrida from './technician-tabs/TabHibrida';
import TabKannegiesser from './technician-tabs/TabKannegiesser';
import PhotoCapture from './PhotoCapture';

const TechnicianTabsWrapper = ({ reportData, user }) => {
    const { t } = useI18n();
    const [tabIndex, setTabIndex] = useState(0);
    const [formData, setFormData] = useState(reportData);
    const [saveStatus, setSaveStatus] = useState('');

    // Стабільна функція збереження
    const stableSaveData = useCallback(async (data) => {
        try {
            await saveData('active_technician_report', data);
        } catch (error) {
            console.error('Error saving report:', error);
        }
    }, []);

    // Зберігаємо дані тільки при зміні formData
    useEffect(() => {
        stableSaveData(formData);
    }, [formData, stableSaveData]);

    const updateData = useCallback((tabKey, newData) => {
        setFormData(prev => ({
            ...prev,
            data: { 
                ...prev.data, 
                [tabKey]: newData 
            }
        }));
    }, []);

    const handlePhotosChange = useCallback((newPhotos) => {
        setFormData(prev => ({
            ...prev,
            photos: newPhotos
        }));
    }, []);

    const handleSave = async () => {
        try {
            await saveData('active_technician_report', formData);
            setSaveStatus(t('common_save_success'));
            setTimeout(() => setSaveStatus(''), 3000);
        } catch (error) {
            setSaveStatus(t('common_save_error'));
        }
    };

    return (
        <Paper elevation={3} sx={{ borderRadius: '12px' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)} variant="scrollable" scrollButtons="auto">
                        <Tab label={t('tech_form_tab_calderas')} />
                        <Tab label={t('tech_form_tab_introductor')} />
                        <Tab label={t('tech_form_tab_hibrida')} />
                        <Tab label={t('tech_form_tab_kannegiesser')} />
                        <Tab 
                            icon={<CameraAltIcon />} 
                            iconPosition="start" 
                            label={t('photos_tab')} 
                        />
                    </Tabs>
                    <Button 
                        startIcon={<SaveIcon />} 
                        onClick={handleSave}
                        variant="contained"
                        sx={{ mr: 2 }}
                    >
                        {t('common_save')}
                    </Button>
                </Stack>
            </Box>
            
            <Box sx={{ p: 3 }}>
                {saveStatus && (
                    <Box sx={{ mb: 2, p: 1, bgcolor: saveStatus.includes('error') ? 'error.light' : 'success.light', borderRadius: 1 }}>
                        {saveStatus}
                    </Box>
                )}
                
                {tabIndex === 0 && (
                    <TabCalderas
                        data={formData.data?.calderas || {}}
                        updateData={(newData) => updateData('calderas', newData)}
                    />
                )}
                {tabIndex === 1 && (
                    <TabIntroductor
                        data={formData.data?.introductor || {}}
                        updateData={(newData) => updateData('introductor', newData)}
                    />
                )}
                {tabIndex === 2 && (
                    <TabHibrida
                        data={formData.data?.hibrida || {}}
                        updateData={(newData) => updateData('hibrida', newData)}
                    />
                )}
                {tabIndex === 3 && (
                    <TabKannegiesser
                        data={formData.data?.kannegiesser || []}
                        updateData={(newData) => updateData('kannegiesser', newData)}
                    />
                )}
                {tabIndex === 4 && (
                    <PhotoCapture
                        onPhotosChange={handlePhotosChange}
                        existingPhotos={formData.photos || []}
                    />
                )}
            </Box>
        </Paper>
    );
};

export default TechnicianTabsWrapper;